import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

/**
 * TVChart на базе TradingView Lightweight Charts™ (open-source, https://github.com/tradingview/lightweight-charts)
 *
 * Почему не встраиваемый виджет tv.js: у публичного бесплатного виджета НЕТ работающих
 * методов createShape / selectLineTool — они есть только в лицензируемой Charting Library.
 * Поэтому линии позиций и рисование "молча" не работали. Здесь график строится напрямую
 * из свечей Bybit (v5 kline API), и мы полностью контролируем:
 *   - линии позиций / TP / SL / лимитных ордеров (createPriceLine) — с подписью на шкале цены;
 *   - собственные инструменты рисования (слой canvas поверх графика).
 *
 * Методы через ref:
 *   selectTool(id)                  — "cursor" | "trend" | "ray" | "hline" | "rect" | "pencil" | "text" | "ruler"
 *   removeAllShapes()               — удалить все нарисованные фигуры текущей пары/таймфрейма
 *   syncMarkers(positions, orders)  — перерисовать линии входа/TP/SL/ордеров
 *
 * Пропсы:
 *   symbol       — "BYBIT:BTCUSDT" (спот) или "BYBIT:BTCUSDT.P" (перпетуал)
 *   timeframe    — "1m" | "5m" | "15m" | "30m" | "1h" | "4h" | "1d" | "1w" | "1M"
 *   onToolChange — вызывается, когда инструмент сбрасывается на курсор после завершения рисования
 *
 * Рисунки хранятся в логических координатах (индекс бара + цена), поэтому корректно
 * следуют за графиком при панорамировании/зуме и сохраняются при переключении пары
 * (для каждой комбинации пара+таймфрейм — свой набор фигур, в памяти сессии).
 */

const TF_TO_BYBIT = {
  "1m": "1",
  "5m": "5",
  "15m": "15",
  "30m": "30",
  "1h": "60",
  "4h": "240",
  "1d": "D",
  "1w": "W",
  "1M": "M",
};

// Таймфреймы в формат Yahoo Finance (интервал + диапазон истории свечей).
// Yahoo не умеет 4h — берём часовые свечи и склеиваем по 4 (aggregate).
const TF_TO_YAHOO = {
  "1m": { interval: "1m", range: "5d" },
  "5m": { interval: "5m", range: "1mo" },
  "15m": { interval: "15m", range: "1mo" },
  "30m": { interval: "30m", range: "1mo" },
  "1h": { interval: "60m", range: "3mo" },
  "4h": { interval: "60m", range: "6mo", aggregate: 4 },
  "1d": { interval: "1d", range: "2y" },
  "1w": { interval: "1wk", range: "5y" },
  "1M": { interval: "1mo", range: "10y" },
};

// Базовый адрес API (совпадает с логикой в App.js): сборочный REACT_APP_API_URL,
// иначе localhost:4000 в dev, иначе тот же адрес, с которого открыт сайт.
const API_URL =
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_API_URL) ||
  (typeof window !== "undefined" && window.location && window.location.port === "3000"
    ? "http://localhost:4000"
    : "");

// Форекс/акция -> символ Yahoo. Форекс: EUR/USD -> EURUSD=X. Акции: точка -> дефис (BRK.B -> BRK-B).
function yahooSymbol(pair, market) {
  if (market === "forex") return pair.replace("/", "").toUpperCase() + "=X";
  return pair.replace(/\./g, "-").toUpperCase();
}

// Склейка N свечей в одну (для таймфреймов, которых нет у Yahoo напрямую, напр. 4h из 1h).
function aggregateBars(bars, n) {
  const out = [];
  for (let i = 0; i < bars.length; i += n) {
    const chunk = bars.slice(i, i + n);
    if (!chunk.length) continue;
    out.push({
      time: chunk[0].time,
      open: chunk[0].open,
      high: Math.max(...chunk.map((b) => b.high)),
      low: Math.min(...chunk.map((b) => b.low)),
      close: chunk[chunk.length - 1].close,
      volume: chunk.reduce((s, b) => s + (b.volume || 0), 0),
    });
  }
  return out;
}

function parseSymbol(symbol) {
  const hasPrefix = symbol.includes(":");
  const prefix = hasPrefix ? symbol.split(":")[0].toUpperCase() : "BYBIT";
  const raw = hasPrefix ? symbol.split(":")[1] : symbol;
  if (prefix === "FX") return { pair: raw.toUpperCase(), category: "forex", market: "forex" };
  if (prefix === "STOCK") return { pair: raw.toUpperCase(), category: "stock", market: "stock" };
  const isFutures = /\.P$/i.test(raw);
  const pair = raw.replace(/\.P$/i, "").toUpperCase();
  return { pair, category: isFutures ? "linear" : "spot", market: "crypto" };
}

// Библиотека грузится один раз на всё приложение (standalone-сборка, без npm-зависимости)
let libPromise = null;
function loadLightweightCharts() {
  if (window.LightweightCharts) return Promise.resolve(window.LightweightCharts);
  if (!libPromise) {
    libPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://unpkg.com/lightweight-charts@4.2.3/dist/lightweight-charts.standalone.production.js";
      s.async = true;
      s.onload = () => resolve(window.LightweightCharts);
      s.onerror = () => {
        libPromise = null;
        reject(new Error("Не удалось загрузить lightweight-charts"));
      };
      document.body.appendChild(s);
    });
  }
  return libPromise;
}

const COLORS = {
  bg: "#010208",
  grid: "#0b1120",
  text: "#94a3b8",
  border: "#1e293b",
  up: "#22c55e",
  down: "#ef4444",
  order: "#1e40af",
  draw: "#1e3a8a",
};

const TVChart = forwardRef(function TVChart({ symbol = "BYBIT:BTCUSDT", timeframe = "30m", livePrice = null, onToolChange }, ref) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const candleRef = useRef(null);
  const volumeRef = useRef(null);
  const lastBarRef = useRef(null); // последняя свеча — для мгновенного обновления по live-цене
  const lastBarSymbolRef = useRef(null); // символ, которому принадлежит lastBarRef (защита при смене пары)
  const priceLinesRef = useRef([]);
  const lastMarkersRef = useRef({ positions: [], orders: [] });

  // Рисование
  const activeToolRef = useRef("cursor");
  const drawColorRef = useRef(COLORS.draw); // текущий цвет рисования
  const draftRef = useRef(null); // фигура в процессе рисования
  const selectedRef = useRef(null); // выделенная фигура (для удаления по клику)
  const shapesMapRef = useRef({}); // { "BYBIT:BTCUSDT|30m": [shape, ...] }
  const keyRef = useRef(`${symbol}|${timeframe}`);
  keyRef.current = `${symbol}|${timeframe}`;
  const onToolChangeRef = useRef(onToolChange);
  onToolChangeRef.current = onToolChange;

  const rafRef = useRef(null);
  const wasDrawnRef = useRef(false);

  const [status, setStatus] = useState("loading"); // loading | ok | error
  const [errMsg, setErrMsg] = useState(""); // текст причины ошибки (например, от Yahoo Finance API)

  const shapesForCurrent = () => {
    if (!shapesMapRef.current[keyRef.current]) shapesMapRef.current[keyRef.current] = [];
    return shapesMapRef.current[keyRef.current];
  };

  // Подбор точности шкалы цены по величине цены (чтобы дешёвые монеты не показывали «0.00»)
  const priceDecimals = (p) => {
    const v = Math.abs(Number(p));
    if (!isFinite(v) || v === 0) return 2;
    if (v >= 1000) return 2;
    if (v >= 1) return 2;
    if (v >= 0.1) return 4;
    if (v >= 0.001) return 6;
    if (v >= 0.00001) return 8;
    return 10;
  };
  function applyPriceFormat(p) {
    const d = priceDecimals(p);
    try {
      candleRef.current?.applyOptions({
        priceFormat: { type: "price", precision: d, minMove: Math.pow(10, -d) },
      });
    } catch (e) {}
  }

  // ------------------------------------------------------------------
  // Линии позиций / TP / SL / ордеров (createPriceLine)
  // ------------------------------------------------------------------
  function applyMarkers() {
    const series = candleRef.current;
    if (!series) return;
    priceLinesRef.current.forEach((l) => {
      try {
        series.removePriceLine(l);
      } catch (e) {}
    });
    priceLinesRef.current = [];

    const LWC = window.LightweightCharts;
    const LS = LWC?.LineStyle || { Solid: 0, Dotted: 1, Dashed: 2 };
    const add = (opts) => {
      try {
        priceLinesRef.current.push(series.createPriceLine(opts));
      } catch (e) {}
    };

    const { positions, orders } = lastMarkersRef.current;

    for (const p of positions || []) {
      const color = p.dir === "buy" ? COLORS.up : COLORS.down;
      add({
        price: p.entry,
        color,
        lineWidth: 2,
        lineStyle: LS.Solid,
        axisLabelVisible: true,
        title: `${p.dir === "buy" ? "Лонг" : "Шорт"} ${p.qty} @ ${p.entry}`,
      });
      if (p.tp) {
        add({ price: p.tp, color: COLORS.up, lineWidth: 1, lineStyle: LS.Dashed, axisLabelVisible: true, title: "TP" });
      }
      if (p.sl) {
        add({ price: p.sl, color: COLORS.down, lineWidth: 1, lineStyle: LS.Dashed, axisLabelVisible: true, title: "SL" });
      }
    }

    for (const o of orders || []) {
      add({
        price: o.limitPrice,
        color: COLORS.order,
        lineWidth: 1,
        lineStyle: LS.Dotted,
        axisLabelVisible: true,
        title: `Ордер ${o.dir === "buy" ? "buy" : "sell"} ${o.qty}`,
      });
    }
  }

  // ------------------------------------------------------------------
  // Слой рисования: преобразование координат
  // ------------------------------------------------------------------
  const toXY = (pt) => {
    const chart = chartRef.current;
    const series = candleRef.current;
    if (!chart || !series) return null;
    const x = chart.timeScale().logicalToCoordinate(pt.logical);
    const y = series.priceToCoordinate(pt.price);
    if (x === null || x === undefined || y === null || y === undefined) return null;
    return { x, y };
  };

  const evtPoint = (e) => {
    const canvas = canvasRef.current;
    const chart = chartRef.current;
    const series = candleRef.current;
    if (!canvas || !chart || !series) return null;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const logical = chart.timeScale().coordinateToLogical(x);
    const price = series.coordinateToPrice(y);
    if (logical === null || logical === undefined || price === null || price === undefined) return null;
    return { logical, price };
  };

  const priceFmt = (p) => {
    const abs = Math.abs(p);
    const d = abs >= 1000 ? 1 : abs >= 100 ? 2 : abs >= 1 ? 3 : abs >= 0.01 ? 5 : 8;
    return p.toFixed(d);
  };

  // ------------------------------------------------------------------
  // Слой рисования: отрисовка фигур
  // ------------------------------------------------------------------
  // Преобразование hex-цвета в rgba со своей прозрачностью
  const hexToRgba = (hex, alpha) => {
    const h = hex.replace("#", "");
    const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  };

  function drawShape(ctx, s, w, h, highlighted) {
    const col = s.color || COLORS.draw;
    ctx.lineWidth = highlighted ? 3 : 1.5;
    ctx.strokeStyle = col;
    ctx.fillStyle = col;
    ctx.font = "11px sans-serif";

    if (s.type === "hline") {
      const y = candleRef.current?.priceToCoordinate(s.price);
      if (y === null || y === undefined) return;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
      ctx.stroke();
      const label = priceFmt(s.price);
      const tw = ctx.measureText(label).width;
      ctx.fillStyle = hexToRgba(col, 0.9);
      ctx.fillRect(w - tw - 10, y - 9, tw + 8, 16);
      ctx.fillStyle = "#010208";
      ctx.fillText(label, w - tw - 6, y + 3);
      return;
    }

    if (s.type === "pencil") {
      const pts = s.points.map(toXY).filter(Boolean);
      if (pts.length < 2) return;
      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);
      pts.slice(1).forEach((p) => ctx.lineTo(p.x, p.y));
      ctx.stroke();
      return;
    }

    if (s.type === "text") {
      const p = toXY(s.a);
      if (!p) return;
      ctx.font = "bold 12px sans-serif";
      ctx.fillStyle = col;
      ctx.fillText(s.text, p.x, p.y);
      return;
    }

    const a = toXY(s.a);
    const b = toXY(s.b);
    if (!a || !b) return;

    if (s.type === "trend") {
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
      return;
    }

    if (s.type === "ray") {
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const len = Math.max(1, Math.hypot(dx, dy));
      const k = ((w + h) * 2) / len; // гарантированно за край холста
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(a.x + dx * k, a.y + dy * k);
      ctx.stroke();
      return;
    }

    if (s.type === "rect") {
      const x = Math.min(a.x, b.x);
      const y = Math.min(a.y, b.y);
      const rw = Math.abs(b.x - a.x);
      const rh = Math.abs(b.y - a.y);
      ctx.fillStyle = hexToRgba(col, 0.12);
      ctx.fillRect(x, y, rw, rh);
      ctx.strokeStyle = col;
      ctx.strokeRect(x, y, rw, rh);
      return;
    }

    if (s.type === "ruler") {
      const up = s.b.price >= s.a.price;
      const base = up ? "34,197,94" : "239,68,68";
      const x = Math.min(a.x, b.x);
      const y = Math.min(a.y, b.y);
      const rw = Math.abs(b.x - a.x);
      const rh = Math.abs(b.y - a.y);
      ctx.fillStyle = `rgba(${base},0.15)`;
      ctx.fillRect(x, y, rw, rh);
      ctx.strokeStyle = `rgb(${base})`;
      ctx.strokeRect(x, y, rw, rh);
      // стрелка по диагонали
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
      // подпись: Δцены, Δ% и количество баров
      const dPrice = s.b.price - s.a.price;
      const dPct = (dPrice / s.a.price) * 100;
      const bars = Math.round(Math.abs(s.b.logical - s.a.logical));
      const label = `${dPrice >= 0 ? "+" : ""}${priceFmt(dPrice)}  (${dPct >= 0 ? "+" : ""}${dPct.toFixed(2)}%)  ${bars} бар.`;
      ctx.font = "bold 11px sans-serif";
      const tw = ctx.measureText(label).width;
      const lx = Math.min(Math.max(4, (a.x + b.x) / 2 - tw / 2), w - tw - 4);
      const ly = up ? y - 8 : y + rh + 16;
      ctx.fillStyle = `rgb(${base})`;
      ctx.fillRect(lx - 4, ly - 11, tw + 8, 16);
      ctx.fillStyle = "#fff";
      ctx.fillText(label, lx, ly + 1);
    }
  }

  function drawAll() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const shapes = shapesMapRef.current[keyRef.current] || [];
    const hasContent = shapes.length > 0 || draftRef.current;
    if (!hasContent && !wasDrawnRef.current) return;

    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.width / dpr;
    const h = canvas.height / dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    wasDrawnRef.current = hasContent;
    if (!hasContent) return;

    shapes.forEach((s) => drawShape(ctx, s, w, h, s === selectedRef.current));
    if (draftRef.current) drawShape(ctx, draftRef.current, w, h, false);
  }

  // ------------------------------------------------------------------
  // Проверка попадания курсора в фигуру (для выделения/удаления по клику)
  // ------------------------------------------------------------------
  const distToSeg = (px, py, x1, y1, x2, y2) => {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const l2 = dx * dx + dy * dy;
    if (l2 === 0) return Math.hypot(px - x1, py - y1);
    let t = ((px - x1) * dx + (py - y1) * dy) / l2;
    t = Math.max(0, Math.min(1, t));
    return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
  };

  const hitTest = (mx, my) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.width / dpr;
    const h = canvas.height / dpr;
    const TOL = 8;
    const shapes = shapesForCurrent();
    // сверху вниз — последняя нарисованная имеет приоритет
    for (let i = shapes.length - 1; i >= 0; i--) {
      const s = shapes[i];
      if (s.type === "hline") {
        const y = candleRef.current?.priceToCoordinate(s.price);
        if (y != null && Math.abs(my - y) <= TOL) return s;
        continue;
      }
      if (s.type === "text") {
        const p = toXY(s.a);
        if (p && Math.abs(mx - p.x) < 60 && Math.abs(my - p.y) < 14) return s;
        continue;
      }
      if (s.type === "pencil") {
        const pts = s.points.map(toXY).filter(Boolean);
        for (let j = 1; j < pts.length; j++) {
          if (distToSeg(mx, my, pts[j - 1].x, pts[j - 1].y, pts[j].x, pts[j].y) <= TOL) return s;
        }
        continue;
      }
      const a = toXY(s.a);
      const b = toXY(s.b);
      if (!a || !b) continue;
      if (s.type === "trend" || s.type === "ray") {
        let ex = b.x, ey = b.y;
        if (s.type === "ray") {
          const dx = b.x - a.x, dy = b.y - a.y;
          const len = Math.max(1, Math.hypot(dx, dy));
          const k = ((w + h) * 2) / len;
          ex = a.x + dx * k; ey = a.y + dy * k;
        }
        if (distToSeg(mx, my, a.x, a.y, ex, ey) <= TOL) return s;
        continue;
      }
      if (s.type === "rect" || s.type === "ruler") {
        const x = Math.min(a.x, b.x), y = Math.min(a.y, b.y);
        const rw = Math.abs(b.x - a.x), rh = Math.abs(b.y - a.y);
        const onEdge =
          (Math.abs(mx - x) <= TOL || Math.abs(mx - (x + rw)) <= TOL) && my >= y - TOL && my <= y + rh + TOL;
        const onEdge2 =
          (Math.abs(my - y) <= TOL || Math.abs(my - (y + rh)) <= TOL) && mx >= x - TOL && mx <= x + rw + TOL;
        const inside = mx >= x && mx <= x + rw && my >= y && my <= y + rh;
        if (onEdge || onEdge2 || inside) return s;
        continue;
      }
    }
    return null;
  };

  const deleteShape = (s) => {
    const arr = shapesForCurrent();
    const idx = arr.indexOf(s);
    if (idx >= 0) arr.splice(idx, 1);
    if (selectedRef.current === s) selectedRef.current = null;
    wasDrawnRef.current = true;
  };

  // ------------------------------------------------------------------
  // Слой рисования: события мыши
  // ------------------------------------------------------------------
  const resetTool = () => {
    activeToolRef.current = "cursor";
    if (canvasRef.current) {
      canvasRef.current.style.pointerEvents = "none";
      canvasRef.current.style.cursor = "default";
      canvasRef.current.style.touchAction = "auto";
    }
    try {
      chartRef.current?.applyOptions({ handleScroll: true, handleScale: true });
    } catch (e) {}
    onToolChangeRef.current?.("cursor");
  };

  const handleMouseDown = (e) => {
    const tool = activeToolRef.current;
    if (tool === "cursor") return;
    // захватываем указатель, чтобы move/up приходили даже за пределами canvas (важно для touch)
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch (_) {}

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;

    // Ластик или выделение: клик по фигуре
    if (tool === "eraser") {
      const hit = hitTest(mx, my);
      if (hit) deleteShape(hit);
      return;
    }
    if (tool === "select") {
      selectedRef.current = hitTest(mx, my);
      wasDrawnRef.current = true;
      return;
    }

    const pt = evtPoint(e);
    if (!pt) return;
    const color = drawColorRef.current;

    if (tool === "hline") {
      shapesForCurrent().push({ type: "hline", price: pt.price, color });
      resetTool();
      return;
    }
    if (tool === "text") {
      const text = window.prompt("Текст надписи:");
      if (text && text.trim()) shapesForCurrent().push({ type: "text", a: pt, text: text.trim(), color });
      resetTool();
      return;
    }
    draftRef.current =
      tool === "pencil"
        ? { type: "pencil", points: [pt], color }
        : { type: tool, a: pt, b: pt, color };
  };

  const handleMouseMove = (e) => {
    const draft = draftRef.current;
    if (!draft) return;
    const pt = evtPoint(e);
    if (!pt) return;
    if (draft.type === "pencil") draft.points.push(pt);
    else draft.b = pt;
  };

  const handleMouseUp = (e) => {
    try { e?.currentTarget?.releasePointerCapture?.(e.pointerId); } catch (_) {}
    const draft = draftRef.current;
    if (!draft) return;
    const meaningful =
      draft.type === "pencil"
        ? draft.points.length > 1
        : draft.a.logical !== draft.b.logical || draft.a.price !== draft.b.price;
    if (meaningful) shapesForCurrent().push(draft);
    draftRef.current = null;
    resetTool();
  };

  // ------------------------------------------------------------------
  // Создание графика (один раз) + загрузка данных (на каждую смену пары/ТФ)
  // ------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    let pollId = null;

    async function fetchKlines(limit) {
      const { pair, category, market } = parseSymbol(symbol);
      // --- Форекс и акции: Yahoo Finance через прокси server.js (Bybit их не отдаёт,
      //     а Yahoo блокирует прямые запросы из браузера — ходим через свой сервер) ---
      if (market === "forex" || market === "stock") {
        const cfg = TF_TO_YAHOO[timeframe] || TF_TO_YAHOO["30m"];
        const ySym = yahooSymbol(pair, market);
        const res = await fetch(
          `${API_URL}/api/market/yahoo?symbol=${encodeURIComponent(ySym)}&interval=${cfg.interval}&range=${cfg.range}`,
          { headers: { "ngrok-skip-browser-warning": "true" } }
        );
        const json = await res.json();
        // Ошибка Yahoo/прокси: { chart: { error: { description } } } или { error }
        const yErr = json?.chart?.error?.description || (json?.error && !json?.chart ? json.error : null);
        if (yErr) {
          const e = new Error(yErr);
          e.tdMessage = yErr;
          throw e;
        }
        const result = json?.chart?.result?.[0];
        const ts = result?.timestamp || [];
        const q = result?.indicators?.quote?.[0] || {};
        let bars = ts
          .map((t, i) => ({
            time: t,
            open: +q.open?.[i],
            high: +q.high?.[i],
            low: +q.low?.[i],
            close: +q.close?.[i],
            volume: q.volume?.[i] != null ? +q.volume[i] : 0,
          }))
          .filter(
            (b) =>
              isFinite(b.time) &&
              isFinite(b.open) && b.open > 0 &&
              isFinite(b.high) && b.high > 0 &&
              isFinite(b.low) && b.low > 0 &&
              isFinite(b.close) && b.close > 0
          )
          .sort((x, y) => x.time - y.time);
        // Yahoo не умеет 4h — склеиваем часовые свечи по 4 (aggregate).
        if (cfg.aggregate && cfg.aggregate > 1) bars = aggregateBars(bars, cfg.aggregate);
        return bars.slice(-limit);
      }
      // --- Крипта: Bybit ---
      const itv = TF_TO_BYBIT[timeframe] || "30";
      const res = await fetch(
        `https://api.bybit.com/v5/market/kline?category=${category}&symbol=${pair}&interval=${itv}&limit=${limit}`
      );
      const json = await res.json();
      const list = json?.result?.list || [];
      return list
        .map((r) => ({
          time: Math.floor(Number(r[0]) / 1000),
          open: +r[1],
          high: +r[2],
          low: +r[3],
          close: +r[4],
          volume: +r[5],
        }))
        .sort((x, y) => x.time - y.time);
    }

    const volBar = (b) => ({
      time: b.time,
      value: b.volume,
      color: b.close >= b.open ? "rgba(34,197,94,0.35)" : "rgba(239,68,68,0.35)",
    });

    (async () => {
      let LWC;
      try {
        LWC = await loadLightweightCharts();
      } catch (e) {
        if (!cancelled) setStatus("error");
        return;
      }
      if (cancelled || !containerRef.current) return;

      // --- создаём график один раз ---
      if (!chartRef.current) {
        const chart = LWC.createChart(containerRef.current, {
          autoSize: true,
          layout: {
            background: { type: "solid", color: COLORS.bg },
            textColor: COLORS.text,
          },
          grid: {
            vertLines: { color: COLORS.grid },
            horzLines: { color: COLORS.grid },
          },
          crosshair: { mode: LWC.CrosshairMode?.Normal ?? 0 },
          rightPriceScale: { borderColor: COLORS.border },
          timeScale: {
            borderColor: COLORS.border,
            timeVisible: true,
            secondsVisible: false,
          },
          localization: { locale: "ru" },
        });

        const candle = chart.addCandlestickSeries({
          upColor: COLORS.up,
          downColor: COLORS.down,
          borderVisible: false,
          wickUpColor: COLORS.up,
          wickDownColor: COLORS.down,
        });

        const volume = chart.addHistogramSeries({
          priceScaleId: "",
          priceFormat: { type: "volume" },
          lastValueVisible: false,
          priceLineVisible: false,
        });
        chart.priceScale("").applyOptions({ scaleMargins: { top: 0.85, bottom: 0 } });

        chartRef.current = chart;
        candleRef.current = candle;
        volumeRef.current = volume;

        // непрерывная перерисовка слоя рисования (дёшево: при отсутствии фигур сразу выходит)
        const loop = () => {
          drawAll();
          rafRef.current = requestAnimationFrame(loop);
        };
        rafRef.current = requestAnimationFrame(loop);
      }

      // --- загрузка истории для текущей пары/таймфрейма ---
      // Сбрасываем последнюю свечу от ПРЕДЫДУЩЕЙ пары, чтобы live-цена не
      // подмешивала её (с чужим ценовым диапазоном) в новый график и не ломала
      // автомасштаб, пока грузится история.
      lastBarRef.current = null;
      lastBarSymbolRef.current = null;
      setStatus("loading");
      try {
        const bars = await fetchKlines(500);
        if (cancelled) return;
        if (!bars.length) {
          setErrMsg("");
          setStatus("error");
          return;
        }
        candleRef.current.setData(bars.map(({ volume, ...b }) => b));
        volumeRef.current.setData(bars.map(volBar));
        lastBarRef.current = (() => { const { volume, ...b } = bars[bars.length - 1]; return b; })();
        lastBarSymbolRef.current = symbol;
        // Точность шкалы цены зависит от масштаба инструмента: у монет вроде 0.0031
        // формат по умолчанию (2 знака) показывал бы «0.00». Считаем по последней цене.
        applyPriceFormat(bars[bars.length - 1].close);
        // Принудительно пересчитываем автомасштаб цены по свежим данным, чтобы
        // остаточный диапазон от предыдущей пары не растягивал шкалу.
        try {
          chartRef.current.priceScale("right").applyOptions({ autoScale: true });
        } catch (e) {}
        chartRef.current.timeScale().scrollToRealTime();
        setStatus("ok");
        setErrMsg("");
        applyMarkers(); // восстановить линии позиций после смены данных
      } catch (e) {
        if (!cancelled) {
          setErrMsg(e && e.tdMessage ? e.tdMessage : "");
          setStatus("error");
        }
        return;
      }

      // --- живое обновление последней свечи ---
      // Крипта (Bybit) — раз в секунду; форекс/акции (Yahoo через прокси) — раз в 5 c.
      // Между опросами график всё равно «дышит» за счёт livePrice из тикеров.
      const { market } = parseSymbol(symbol);
      const pollMs = market === "forex" || market === "stock" ? 5000 : 1000;
      pollId = setInterval(async () => {
        try {
          const bars = await fetchKlines(2);
          if (cancelled || !bars.length) return;
          bars.forEach((b) => {
            const { volume, ...candleBar } = b;
            candleRef.current.update(candleBar);
            volumeRef.current.update(volBar(b));
          });
          const { volume, ...lastBar } = bars[bars.length - 1];
          lastBarRef.current = lastBar;
        } catch (e) {}
      }, pollMs);
    })();

    return () => {
      cancelled = true;
      if (pollId) clearInterval(pollId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, timeframe]);

  // Мгновенное обновление последней свечи по live-цене из App — график «дышит»
  // на каждый тик цены, не дожидаясь следующего опроса klines (раз в секунду).
  useEffect(() => {
    const price = Number(livePrice);
    const bar = lastBarRef.current;
    if (!candleRef.current || !bar || !isFinite(price) || price <= 0) return;
    // Защита при смене пары: бар должен принадлежать текущему символу, иначе
    // live-цена новой пары подмешалась бы в свечу старой (или наоборот) и
    // растянула бы автомасштаб. Пока история новой пары не загрузилась — не трогаем.
    if (lastBarSymbolRef.current !== symbol) return;
    // Отсев явных выбросов: тик, отличающийся от close свечи более чем в 5 раз
    // (или наоборот в 5 раз меньше), — это битые данные, а не движение рынка.
    if (bar.close > 0 && (price > bar.close * 5 || price < bar.close / 5)) return;
    const updated = {
      ...bar,
      close: price,
      high: Math.max(bar.high, price),
      low: Math.min(bar.low, price),
    };
    lastBarRef.current = updated;
    try {
      candleRef.current.update(updated);
    } catch (e) {}
  }, [livePrice, symbol]);

  // Размер canvas-слоя поверх графика + уничтожение графика при размонтировании
  useEffect(() => {
    const container = containerRef.current?.parentElement;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const { width, height } = container.getBoundingClientRect();
      canvas.width = Math.max(1, Math.round(width * dpr));
      canvas.height = Math.max(1, Math.round(height * dpr));
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      wasDrawnRef.current = true; // принудительная перерисовка после ресайза
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(container);

    return () => {
      ro.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      try {
        chartRef.current?.remove();
      } catch (e) {}
      chartRef.current = null;
      candleRef.current = null;
      volumeRef.current = null;
    };
  }, []);

  // ------------------------------------------------------------------
  // Внешний API (через ref)
  // ------------------------------------------------------------------
  useImperativeHandle(ref, () => ({
    selectTool(toolName) {
      const tool = toolName || "cursor";
      activeToolRef.current = tool;
      draftRef.current = null;
      if (tool === "cursor") selectedRef.current = null;
      const drawing = tool !== "cursor";
      if (canvasRef.current) {
        canvasRef.current.style.pointerEvents = drawing ? "auto" : "none";
        canvasRef.current.style.cursor =
          tool === "eraser" ? "cell" : tool === "select" ? "pointer" : drawing ? "crosshair" : "default";
        // на телефоне отключаем прокрутку страницы/жесты, пока активен инструмент
        canvasRef.current.style.touchAction = drawing ? "none" : "auto";
      }
      // блокируем панорамирование/зум графика во время рисования (иначе на телефоне двигается график)
      try {
        chartRef.current?.applyOptions({
          handleScroll: !drawing,
          handleScale: !drawing,
        });
      } catch (e) {}
      wasDrawnRef.current = true;
    },
    setColor(color) {
      if (color) drawColorRef.current = color;
      // если что-то выделено — перекрасить выделенную фигуру
      if (selectedRef.current) {
        selectedRef.current.color = color;
        wasDrawnRef.current = true;
      }
    },
    removeSelected() {
      if (selectedRef.current) {
        deleteShape(selectedRef.current);
        return true;
      }
      return false;
    },
    removeAllShapes() {
      shapesMapRef.current[keyRef.current] = [];
      draftRef.current = null;
      selectedRef.current = null;
      wasDrawnRef.current = true; // очистить холст на следующем кадре
    },
    syncMarkers(positions, orders) {
      lastMarkersRef.current = { positions: positions || [], orders: orders || [] };
      applyMarkers();
    },
    setVolumeVisible(visible) {
      try {
        volumeRef.current?.applyOptions({ visible: !!visible });
      } catch (e) {}
    },
    setLogScale(on) {
      try {
        chartRef.current?.priceScale("right").applyOptions({ mode: on ? 1 : 0 });
      } catch (e) {}
    },
    fitContent() {
      try {
        chartRef.current?.timeScale().fitContent();
      } catch (e) {}
    },
  }));

  return (
    <div className="relative w-full h-full">
      <div ref={containerRef} className="absolute inset-0" />
      <canvas
        ref={canvasRef}
        onPointerDown={handleMouseDown}
        onPointerMove={handleMouseMove}
        onPointerUp={handleMouseUp}
        onPointerCancel={handleMouseUp}
        className="absolute inset-0 z-10"
        style={{ pointerEvents: "none", touchAction: "auto" }}
      />
      {status === "loading" && (
        <div className="absolute inset-0 z-20 flex items-center justify-center text-slate-600 text-sm bg-slate-950/60 pointer-events-none">
          Загрузка графика…
        </div>
      )}
      {status === "error" && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 text-slate-500 text-sm px-6 text-center pointer-events-none">
          <div>Не удалось загрузить данные по инструменту. Проверьте тикер и подключение к сети.</div>
          {errMsg && <div className="text-slate-400 text-xs max-w-md">{errMsg}</div>}
        </div>
      )}
      {/* Обязательная атрибуция библиотеки TradingView Lightweight Charts™ */}
      <a
        href="https://www.tradingview.com/"
        target="_blank"
        rel="noopener noreferrer"
        className="absolute left-1 bottom-1 z-20 text-[9px] text-slate-700 hover:text-slate-500"
      >
        Charts by TradingView
      </a>
    </div>
  );
});

export default TVChart;
